import { useEffect, useState } from 'react'
import { validatePhoneNumber } from '../../services/apiService'
import { useTranslation } from '../../utils/translations'
import { FaSms, FaPhone } from 'react-icons/fa'
import LanguageToggle from '../Common/LanguageToggle'
import { LoadingOverlay, LoadingSpinner } from '../Common/LoadingSpinner'
import './PhoneLogin.css'

const PhoneLogin = ({ onSubmit, error, isLoading, sendCooldownUntil = 0 }) => {
  const [phone, setPhone] = useState('')
  const [cooldownMs, setCooldownMs] = useState(0)
  const [pendingMethod, setPendingMethod] = useState(null)
  const { t, tMessage } = useTranslation()

  useEffect(() => {
    const tick = () => {
      setCooldownMs(Math.max(0, sendCooldownUntil - Date.now()))
    }
    tick()
    if (!sendCooldownUntil || sendCooldownUntil <= Date.now()) {
      return undefined
    }
    const id = setInterval(tick, 250)
    return () => clearInterval(id)
  }, [sendCooldownUntil])

  useEffect(() => {
    if (!isLoading) {
      setPendingMethod(null)
    }
  }, [isLoading])

  const handleSubmit = (method) => {
    if (isLoading || cooldownMs > 0) {
      return
    }
    if (validatePhoneNumber(phone) && method) {
      setPendingMethod(method)
      onSubmit(phone, method)
    }
  }

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '')
    
    // Don't format if empty
    if (!cleaned) return ''
    
    // Apply formatting based on length
    if (cleaned.length <= 3) {
      return cleaned
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`
    }
  }

  const handlePhoneChange = (e) => {
    const inputValue = e.target.value
    
    // Extract only digits
    const digits = inputValue.replace(/\D/g, '')
    
    // Limit to 10 digits max
    if (digits.length <= 10) {
      const formatted = formatPhoneNumber(digits)
      setPhone(formatted)
    }
  }

  const renderMethodButton = (method, Icon, label) => {
    const isPending = isLoading && pendingMethod === method
    return (
      <button 
        type="button"
        className={`method-button${isPending ? ' method-button-loading' : ''}`}
        onClick={() => handleSubmit(method)}
        disabled={!validatePhoneNumber(phone) || isLoading || cooldownMs > 0}
        aria-busy={isPending}
      >
        {isPending ? (
          <LoadingSpinner size="sm" />
        ) : (
          <Icon className="method-button-icon" />
        )}
        <span>{isPending ? t('auth.sendingCode') : label}</span>
      </button>
    )
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-top-controls">
          <div></div>
          <LanguageToggle />
        </div>
        
        <div className="login-header">
          <img src="/logo.svg" alt={t('common.logoAlt')} className="login-logo" />
          <h1 className="login-title">{t('auth.signInTitle')}</h1>
          <p className="login-subtitle">{t('auth.signInSubtitle')}</p>
        </div>
        
        <form className="login-form" aria-busy={isLoading}>
          <div className="form-group">
            <label htmlFor="phone" className="form-label">{t('auth.phoneLabel')}</label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={handlePhoneChange}
              placeholder={t('auth.phonePlaceholder')}
              className="form-input"
              maxLength={14}
              required
              disabled={isLoading}
            />
            {error && <p className="error-text">{tMessage(error)}</p>}
          </div>
          
          <div className="method-selection">
            <p className="method-selection-label">{t('auth.selectMethodSubtitle')}</p>
            <div className="method-buttons">
              {renderMethodButton('sms', FaSms, t('auth.textMethod'))}
              {renderMethodButton('call', FaPhone, t('auth.callMethod'))}
            </div>
            {!isLoading && cooldownMs > 0 && (
              <p className="error-text">{t('auth.waitSeconds', { seconds: Math.ceil(cooldownMs / 1000) })}</p>
            )}
          </div>
        </form>
        {isLoading && <LoadingOverlay message={t('auth.sendingCode')} />}
      </div>
    </div>
  )
}

export default PhoneLogin
